import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Grid,
  GridItem,
  HelperText,
  HelperTextItem,
  Spinner,
  Stack,
  StackItem,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, ServerIcon, SyncAltIcon } from '@patternfly/react-icons';
import type { DomainName, NameserverGlue } from '../../../types/models';
import { useEffectiveNameservers } from '../../../hooks/useEffectiveNameservers';
import { useRefreshRegistryNameservers } from '../../../hooks/useRefreshRegistryNameservers';
import ConfigureNameserversModal from './ConfigureNameserversModal';

interface DomainNameserversTabProps {
  domainName: DomainName;
  onConfigured: () => void;
}

// The backend's exact blocked-action message while a domain is mid-transfer — surfaced verbatim.
const NS_TRANSFER_BLOCKED_MESSAGE =
  'This domain is being transferred in — the nameserver delegation cannot be changed until the transfer completes.';

interface NameserverRow {
  host: string;
  ipv4: string | null;
  ipv6: string | null;
}

function toRows(map: Record<string, NameserverGlue | null> | null): NameserverRow[] {
  return Object.entries(map ?? {})
    .map(([host, glue]) => ({ host, ipv4: glue?.ipv4 ?? null, ipv6: glue?.ipv6 ?? null }))
    .sort((a, b) => a.host.localeCompare(b.host, undefined, { sensitivity: 'base' }));
}

type NameserverMap = Record<string, NameserverGlue | null>;

/** The five sync states — green/warning only when both sides are known; neutral otherwise. */
type NameserverSyncStatus = 'loading' | 'unknown' | 'unconfigured' | 'in_sync' | 'out_of_sync';

/** Lowercase, trim, drop a single trailing dot — for order/case/dot-insensitive host comparison. */
function normalizeHost(host: string): string {
  return host.trim().toLowerCase().replace(/\.$/, '');
}

/**
 * Canonicalize an IP for representation-independent comparison. IPv4 is compared verbatim
 * (lowercased/trimmed). IPv6 is expanded to eight zero-padded hextets so that, e.g.,
 * "2603:5:2150::2" and "2603:0005:2150:0000:0000:0000:0000:0002" compare equal. Anything that
 * does not parse cleanly falls back to the raw lowercased string (still comparable, just not
 * canonicalized). Returns null for an absent/empty value.
 */
function normalizeIp(ip?: string): string | null {
  if (!ip) {
    return null;
  }
  const value = ip.trim().toLowerCase();
  if (value === '') {
    return null;
  }
  if (!value.includes(':')) {
    return value; // IPv4 (or anything non-IPv6) — compare as-is
  }
  const parts = value.split('::');
  if (parts.length > 2) {
    return value; // malformed — fall back to raw
  }
  const head = parts[0] ? parts[0].split(':') : [];
  const tail = parts.length === 2 ? (parts[1] ? parts[1].split(':') : []) : null;
  let groups: string[];
  if (tail === null) {
    groups = head; // no "::" — expect 8 groups already
  } else {
    const missing = 8 - head.length - tail.length;
    if (missing < 0) {
      return value;
    }
    groups = [...head, ...Array<string>(missing).fill('0'), ...tail];
  }
  if (groups.length !== 8) {
    return value;
  }
  return groups.map((group) => (group === '' ? '0' : group).padStart(4, '0')).join(':');
}

/** Per-family glue equality (ipv4 + ipv6), with IPv6 canonicalized. Absent === absent. */
function glueEqual(a: NameserverGlue | null, b: NameserverGlue | null): boolean {
  return normalizeIp(a?.ipv4) === normalizeIp(b?.ipv4) && normalizeIp(a?.ipv6) === normalizeIp(b?.ipv6);
}

/**
 * True iff the registrar delegation matches the live resolution:
 *  - identical NS hostname sets, AND
 *  - for every IN-BAILIWICK registry entry (non-null glue), its declared glue equals the
 *    effective glue for that host (per family; IPv6 representation-independent).
 * OUT-OF-BAILIWICK registry entries (null glue) are matched on hostname only — the effective
 * side's resolved IPs are not registry glue and must not be compared.
 */
function nameserversInSync(registry: NameserverMap, effective: NameserverMap): boolean {
  const reg = new Map<string, NameserverGlue | null>();
  for (const [host, glue] of Object.entries(registry)) {
    reg.set(normalizeHost(host), glue);
  }
  const eff = new Map<string, NameserverGlue | null>();
  for (const [host, glue] of Object.entries(effective)) {
    eff.set(normalizeHost(host), glue);
  }

  if (reg.size !== eff.size) {
    return false;
  }
  // Array.from to iterate the Map — the project's TS target needs it (matches ConfigureNameserversModal).
  for (const [host, registryGlue] of Array.from(reg)) {
    if (!eff.has(host)) {
      return false;
    }
    // Glue compared ONLY for in-bailiwick entries (registry declares glue ⇒ non-null).
    if (registryGlue !== null && !glueEqual(registryGlue, eff.get(host) ?? null)) {
      return false;
    }
  }
  return true;
}

/** Shared read-only table for a nameserver map (host → {ipv4?, ipv6?} | null). */
function NameserverTable({
  nameservers,
  ariaLabel,
  emptyTitle,
  emptyBody,
}: {
  nameservers: Record<string, NameserverGlue | null> | null;
  ariaLabel: string;
  emptyTitle: string;
  emptyBody: string;
}) {
  const rows = useMemo(() => toRows(nameservers), [nameservers]);

  if (rows.length === 0) {
    return (
      <EmptyState titleText={emptyTitle} icon={ServerIcon} variant="sm" headingLevel="h3">
        <EmptyStateBody>{emptyBody}</EmptyStateBody>
      </EmptyState>
    );
  }

  return (
    <Table aria-label={ariaLabel} variant="compact">
      <Thead>
        <Tr>
          <Th>Nameserver</Th>
          <Th>IPv4</Th>
          <Th>IPv6</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row) => (
          <Tr key={row.host}>
            <Td dataLabel="Nameserver">{row.host}</Td>
            <Td dataLabel="IPv4">{row.ipv4 ?? '—'}</Td>
            <Td dataLabel="IPv6">{row.ipv6 ?? '—'}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

function DomainNameserversTab({ domainName, onConfigured }: DomainNameserversTabProps) {
  const [isConfigureOpen, setIsConfigureOpen] = useState(false);

  // Effective resolution = live recursive lookup of the apex NS RRSet (backend
  // GET …/nameservers/refresh). The parent Tabs use mountOnEnter, so this fires only
  // when the Nameservers tab is first opened; Refresh re-runs it.
  const {
    nameservers: effectiveNameservers,
    loading: effectiveLoading,
    error: effectiveError,
    refetch: refetchEffective,
  } = useEffectiveNameservers(domainName.id ?? 0);

  // Registry Configuration = the stored registrar delegation. The refresh action re-reads it from
  // the registrar (backend POST …/nameservers/registry/refresh) and persists; it holds no data of
  // its own — on success the parent reloads the whole domain so registration_nameservers stays
  // single-sourced from the domainName prop.
  const {
    refreshing: registryRefreshing,
    error: registryError,
    refresh: refreshRegistry,
  } = useRefreshRegistryNameservers(domainName.id ?? 0);

  // One click → refresh BOTH panels. The two calls are independent: each updates its own panel and
  // surfaces its own error, so a failure in one never blocks or hides the other. On a successful
  // registry refresh, reload the whole domain (via the existing onConfigured) so the Registry
  // Configuration panel reflects the newly-stored registration_nameservers — the same path
  // ConfigureNameserversModal uses. On failure, registryError is shown in that panel and the stored
  // config is unchanged.
  const handleRefreshNameservers = useCallback(async () => {
    void refetchEffective();
    const ok = await refreshRegistry();
    if (ok) {
      onConfigured();
    }
  }, [refetchEffective, refreshRegistry, onConfigured]);

  // Monitoring sync status: compare the stored registrar delegation against the live effective
  // resolution. Only ever a green/warning verdict when BOTH sides are known — loading, an effective
  // error/null, or a null registration_nameservers each fall back to a neutral state so we never
  // show a misleading verdict.
  const syncStatus: NameserverSyncStatus = useMemo(() => {
    if (effectiveLoading) {
      return 'loading';
    }
    if (effectiveError || effectiveNameservers === null) {
      return 'unknown';
    }
    if (domainName.registration_nameservers === null) {
      return 'unconfigured';
    }
    return nameserversInSync(domainName.registration_nameservers, effectiveNameservers) ? 'in_sync' : 'out_of_sync';
  }, [effectiveLoading, effectiveError, effectiveNameservers, domainName.registration_nameservers]);

  return (
    <>
      <Stack hasGutter>
        {/* 1. Top, full-width recap / monitoring card — shows the nameserver sync status. */}
        <StackItem>
          <Card>
            <CardHeader
              actions={{
                actions: (
                  <>
                    {domainName.in_transfer ? (
                      <Tooltip content={NS_TRANSFER_BLOCKED_MESSAGE}>
                        <Button variant="primary" isAriaDisabled>
                          Configure nameservers
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button variant="primary" onClick={() => setIsConfigureOpen(true)}>
                        Configure nameservers
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      icon={<SyncAltIcon />}
                      isLoading={effectiveLoading || registryRefreshing}
                      onClick={() => {
                        if (!effectiveLoading && !registryRefreshing) {
                          void handleRefreshNameservers();
                        }
                      }}
                    >
                      Refresh nameservers
                    </Button>
                  </>
                ),
              }}
            >
              <CardTitle>Monitoring</CardTitle>
            </CardHeader>
            <CardBody>
              {domainName.in_transfer && (
                <Alert variant="warning" isInline title="Transfer in progress" className="pf-v6-u-mb-md">
                  {NS_TRANSFER_BLOCKED_MESSAGE}
                </Alert>
              )}

              {syncStatus === 'loading' && <Content component="p">Checking nameserver status…</Content>}

              {syncStatus === 'unknown' && (
                <Alert variant="info" isInline isPlain title="Nameserver sync status unavailable">
                  The effective nameservers could not be resolved, so the registrar configuration could not be compared.
                  Use the Refresh nameservers action to try again.
                </Alert>
              )}

              {syncStatus === 'unconfigured' && (
                <Alert variant="info" isInline isPlain title="No registrar configuration recorded">
                  No registrar nameservers have been recorded for this domain yet, so there is nothing to compare
                  against the live resolution.
                </Alert>
              )}

              {syncStatus === 'in_sync' && (
                <Alert variant="success" isInline isPlain title="Nameservers are in sync">
                  The registrar nameserver delegation matches the live public-DNS resolution, including glue for
                  in-bailiwick nameservers.
                </Alert>
              )}

              {syncStatus === 'out_of_sync' && (
                <Alert variant="warning" isInline title="Nameservers are out of sync">
                  The registrar nameservers do not match what is currently resolving from public DNS. This is usually
                  DNS propagation in progress and should resolve within 24 hours. If it has not resolved after that,
                  contact Ancalagon Support to investigate.
                </Alert>
              )}
            </CardBody>
          </Card>
        </StackItem>

        {/* 2. Two cards side by side. */}
        <StackItem>
          <Grid hasGutter>
            {/* Effective resolution — LIVE, from the resolver route. */}
            <GridItem span={12} md={6}>
              <Card isFullHeight>
                <CardTitle>Effective resolution</CardTitle>
                <CardBody>
                  <HelperText className="pf-v6-u-mb-md">
                    <HelperTextItem>
                      The nameservers below are the effective nameservers for this domain, resolved live from public
                      DNS.
                    </HelperTextItem>
                  </HelperText>
                  {effectiveLoading ? (
                    <Bullseye>
                      <Spinner size="lg" aria-label="Resolving nameservers" />
                    </Bullseye>
                  ) : effectiveError ? (
                    <EmptyState
                      titleText="Couldn't resolve nameservers"
                      icon={ExclamationCircleIcon}
                      variant="sm"
                      headingLevel="h3"
                      status="danger"
                    >
                      <EmptyStateBody>{effectiveError}</EmptyStateBody>
                      <EmptyStateFooter>
                        <EmptyStateActions>
                          <Button variant="link" onClick={() => void refetchEffective()}>
                            Retry
                          </Button>
                        </EmptyStateActions>
                      </EmptyStateFooter>
                    </EmptyState>
                  ) : (
                    <NameserverTable
                      nameservers={effectiveNameservers}
                      ariaLabel="Effective nameservers"
                      emptyTitle="No effective nameservers"
                      emptyBody="No NS records resolved for this domain's apex."
                    />
                  )}
                </CardBody>
              </Card>
            </GridItem>

            {/* Registry Configuration — the stored registrar config (registration_nameservers). */}
            <GridItem span={12} md={6}>
              <Card isFullHeight>
                <CardTitle>Registry Configuration</CardTitle>
                <CardBody>
                  <HelperText className="pf-v6-u-mb-md">
                    <HelperTextItem>These are the nameservers configured on the registry.</HelperTextItem>
                  </HelperText>
                  {registryError && (
                    <Alert
                      variant="danger"
                      isInline
                      title="Couldn't refresh registry nameservers"
                      className="pf-v6-u-mb-md"
                    >
                      {registryError}
                    </Alert>
                  )}
                  <NameserverTable
                    nameservers={domainName.registration_nameservers}
                    ariaLabel="Registry nameservers"
                    emptyTitle="No registry configuration"
                    emptyBody="No registrar nameservers have been recorded for this domain yet. Try Refresh nameservers above to fetch them from the registry, and if needed, use Configure nameservers to set them."
                  />
                </CardBody>
              </Card>
            </GridItem>
          </Grid>
        </StackItem>
      </Stack>

      <ConfigureNameserversModal
        isOpen={isConfigureOpen}
        onClose={() => setIsConfigureOpen(false)}
        domainId={domainName.id ?? 0}
        apex={domainName.name}
        initial={domainName.registration_nameservers ?? null}
        currentDnsEndpointId={domainName.dns_endpoint_id}
        onConfigured={onConfigured}
      />
    </>
  );
}

export default DomainNameserversTab;
